import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  HelpCircle, Plus, Save, Trash2, ChevronUp, ChevronDown,
  AlertTriangle, CheckCircle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const FAQTab = ({ onMessage }) => {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [reordering, setReordering] = useState(false);
  const [newFaq, setNewFaq] = useState({ question_en: '', answer_en: '', question_no: '', answer_no: '' });
  const [creating, setCreating] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/faqs`, { withCredentials: true });
      setFaqs(data.map(f => ({ ...f, _dirty: false })));
    } catch (err) {
      onMessage?.(err.response?.data?.detail || 'Failed to load FAQs');
    } finally {
      setLoading(false);
    }
  };

  const updateField = (id, field, value) => {
    setFaqs(prev => prev.map(f => f.id === id ? { ...f, [field]: value, _dirty: true } : f));
  };

  const save = async (faq) => {
    setSavingId(faq.id);
    try {
      const payload = {
        question_en: faq.question_en,
        answer_en: faq.answer_en,
        question_no: faq.question_no,
        answer_no: faq.answer_no,
      };
      const { data } = await axios.put(`${API}/admin/faqs/${faq.id}`, payload, { withCredentials: true });
      setFaqs(prev => prev.map(f => f.id === faq.id ? { ...data, _dirty: false } : f));
      onMessage?.('FAQ saved');
    } catch (err) {
      onMessage?.(err.response?.data?.detail || 'Save failed');
    } finally {
      setSavingId(null);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this FAQ permanently?')) return;
    try {
      await axios.delete(`${API}/admin/faqs/${id}`, { withCredentials: true });
      setFaqs(prev => prev.filter(f => f.id !== id));
      onMessage?.('FAQ deleted');
    } catch (err) {
      onMessage?.(err.response?.data?.detail || 'Delete failed');
    }
  };

  const move = async (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= faqs.length) return;
    const next = [...faqs];
    [next[index], next[newIndex]] = [next[newIndex], next[index]];
    setFaqs(next);
    setReordering(true);
    try {
      await axios.post(`${API}/admin/faqs/reorder`, { ids: next.map(f => f.id) }, { withCredentials: true });
    } catch (err) {
      onMessage?.(err.response?.data?.detail || 'Reorder failed');
      load();
    } finally {
      setReordering(false);
    }
  };

  const create = async () => {
    if (!newFaq.question_en.trim() || !newFaq.answer_en.trim()) {
      onMessage?.('English question + answer are required');
      return;
    }
    setCreating(true);
    try {
      const { data } = await axios.post(`${API}/admin/faqs`, newFaq, { withCredentials: true });
      setFaqs(prev => [...prev, { ...data, _dirty: false }]);
      setNewFaq({ question_en: '', answer_en: '', question_no: '', answer_no: '' });
      onMessage?.('FAQ added');
    } catch (err) {
      onMessage?.(err.response?.data?.detail || 'Add failed');
    } finally {
      setCreating(false);
    }
  };

  if (loading) return <div className="p-6 text-slate-500" data-testid="faq-tab-loading">Loading FAQs…</div>;

  return (
    <div data-testid="admin-faq-tab" className="space-y-6">
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
        <div className="flex items-start gap-3">
          <HelpCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <h2 className="text-xl font-bold text-slate-900">FAQ Manager</h2>
            <p className="text-sm text-slate-600 mt-1">
              Edit, add, reorder and delete the questions visitors see on <code className="px-1 bg-slate-100 rounded">/faq</code> and on the home / signup pages. Each FAQ supports English + Norwegian.
            </p>
          </div>
        </div>
      </div>

      {/* Existing FAQs */}
      {faqs.map((faq, i) => (
        <div
          key={faq.id}
          data-testid={`admin-faq-${faq.id}`}
          className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6"
        >
          <div className="flex items-start justify-between mb-4 gap-3">
            <div className="flex items-center gap-2 text-xs text-slate-500 font-mono">
              <span className="px-2 py-0.5 bg-slate-100 rounded">#{i + 1}</span>
              {faq._dirty && (
                <span className="px-2 py-0.5 bg-amber-100 text-amber-800 rounded inline-flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" /> Unsaved
                </span>
              )}
              {!faq._dirty && (
                <span className="px-2 py-0.5 bg-green-100 text-green-800 rounded inline-flex items-center gap-1">
                  <CheckCircle className="w-3 h-3" /> Saved
                </span>
              )}
            </div>
            <div className="flex items-center gap-1">
              <button
                data-testid={`admin-faq-${faq.id}-up`}
                onClick={() => move(i, -1)}
                disabled={i === 0 || reordering}
                className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-30"
                title="Move up"
              ><ChevronUp className="w-4 h-4" /></button>
              <button
                data-testid={`admin-faq-${faq.id}-down`}
                onClick={() => move(i, 1)}
                disabled={i === faqs.length - 1 || reordering}
                className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-30"
                title="Move down"
              ><ChevronDown className="w-4 h-4" /></button>
              <button
                data-testid={`admin-faq-${faq.id}-delete`}
                onClick={() => remove(faq.id)}
                className="p-1.5 rounded-lg text-red-500 hover:bg-red-50"
                title="Delete"
              ><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {['en', 'no'].map(lang => (
              <div key={lang} className="space-y-2">
                <p className="text-xs font-semibold text-slate-500 flex items-center gap-1.5">
                  <span className="text-base">{lang === 'en' ? '🇬🇧' : '🇳🇴'}</span>
                  {lang === 'en' ? 'English' : 'Norsk'}
                </p>
                <input
                  data-testid={`admin-faq-${faq.id}-${lang}-q`}
                  value={faq[`question_${lang}`] || ''}
                  onChange={(e) => updateField(faq.id, `question_${lang}`, e.target.value)}
                  placeholder="Question"
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 bg-slate-50 text-sm font-medium focus:ring-2 focus:ring-amber-400 outline-none"
                />
                <textarea
                  data-testid={`admin-faq-${faq.id}-${lang}-a`}
                  rows={5}
                  value={faq[`answer_${lang}`] || ''}
                  onChange={(e) => updateField(faq.id, `answer_${lang}`, e.target.value)}
                  placeholder="Answer"
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 bg-slate-50 text-sm leading-relaxed focus:ring-2 focus:ring-amber-400 outline-none"
                />
              </div>
            ))}
          </div>

          <div className="flex justify-end mt-4">
            <button
              data-testid={`admin-faq-${faq.id}-save`}
              onClick={() => save(faq)}
              disabled={!faq._dirty || savingId === faq.id}
              className="inline-flex items-center gap-2 px-5 py-2 bg-amber-500 text-white rounded-xl font-semibold hover:bg-amber-600 disabled:opacity-40"
            >
              <Save className="w-4 h-4" />
              {savingId === faq.id ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      ))}

      {/* Add new */}
      <div className="bg-amber-50 border-2 border-dashed border-amber-300 rounded-2xl p-6">
        <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
          <Plus className="w-5 h-5 text-amber-600" /> Add a new FAQ
        </h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {['en', 'no'].map(lang => (
            <div key={lang} className="space-y-2">
              <p className="text-xs font-semibold text-slate-500 flex items-center gap-1.5">
                <span className="text-base">{lang === 'en' ? '🇬🇧' : '🇳🇴'}</span>
                {lang === 'en' ? 'English (required)' : 'Norsk (optional)'}
              </p>
              <input
                data-testid={`admin-faq-new-${lang}-q`}
                value={newFaq[`question_${lang}`]}
                onChange={(e) => setNewFaq({ ...newFaq, [`question_${lang}`]: e.target.value })}
                placeholder="Question"
                className="w-full px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm font-medium focus:ring-2 focus:ring-amber-400 outline-none"
              />
              <textarea
                data-testid={`admin-faq-new-${lang}-a`}
                rows={4}
                value={newFaq[`answer_${lang}`]}
                onChange={(e) => setNewFaq({ ...newFaq, [`answer_${lang}`]: e.target.value })}
                placeholder="Answer"
                className="w-full px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm leading-relaxed focus:ring-2 focus:ring-amber-400 outline-none"
              />
            </div>
          ))}
        </div>
        <div className="flex justify-end mt-4">
          <button
            data-testid="admin-faq-new-create"
            onClick={create}
            disabled={creating}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-slate-900 text-white rounded-xl font-semibold hover:bg-slate-800 disabled:opacity-40"
          >
            <Plus className="w-4 h-4" />
            {creating ? 'Adding…' : 'Add FAQ'}
          </button>
        </div>
      </div>
    </div>
  );
};
